/**
 * Answers: Is an action allowed?
 */
export type Allowance = {
  can: PolicyAction;
};

/**
 * Answers: Is an action not allowed?
 */
export type Disallowance = {
  cannot: PolicyAction;
};

/**
 * Policies can be set at two levels:
 * - board: applies to interactions across the entire board
 * - interaction: applies to a specific interaction
 */
export type PolicyAction = string & { readonly __policy: unique symbol };

export const policyAction = (p: string): PolicyAction => p as PolicyAction;

/**
 * A policy must have only ONE defined outcome. You can either allow an action
 * to happen, or not allow an action to happen. If you want to define multiple
 * outcomes, don't - make another policy instead. This keeps them small,
 * testable, and reduces the probability of unintended errors.
 */
export type PolicyOutcome = Allowance | Disallowance | undefined;

/**
 * A rule is responsible for producing an outcome based upon the current state
 * of permissions a user has. These outcomes are then collapsed into one final
 * result, which is used by consumers of these methods.
 */
type Rule = (state: Set<PolicyAction>) => PolicyOutcome;

type PolicyDefinition = {
  /**
   * Unique identifier for this policy, based on which it can be enabled or disabled.
   */
  id: string;
  /**
   * An tag for identifying this policy as part of a group of policies.
   * @description Used by different modes of applications to add and remove
   * groups of policies as they are enabled or disabled.
   */
  tag?: string;
  /**
   * Evaluates the rule defining a policy to produce an outcome.
   * @param state current permissions state, containing current claims the user has
   * @returns outcome of the policy evaluation
   */
  rule: Rule;
};

export interface PersistentPolicy extends PolicyDefinition {
  /**
   * Type of policy, determining behaviours around it.
   * @description persistent policies cannot be added or removed during
   * application runtime. They are setup at the very start of the application.
   */
  type: 'persistent';
}

export interface TransientPolicy extends PolicyDefinition {
  /**
   * Type of policy, determining behaviours around it.
   * @description transient policies can be added or removed during application runtime.
   */
  type: 'transient';
}

/**
 * Policies can be either transient or persistent. They enforce a rule based
 * upon {@link PolicyAction}, to allow or disallow specific interactions.
 */
export type Policy = PersistentPolicy | TransientPolicy;

/**
 * An outcome which 'allows' an action.
 * @param outcome
 * @returns
 */
export const isAllowance = (outcome: PolicyOutcome): outcome is Allowance => {
  return Boolean(outcome && 'can' in outcome);
};

/**
 * * An outcome which 'disallows' an action.
 * @param outcome
 * @returns
 */
export const isDisallowance = (outcome: PolicyOutcome): outcome is Disallowance => {
  return Boolean(outcome && 'cannot' in outcome);
};
